import jsonExport from "jsonexport/dist";
import {
  downloadCSV,
  FilterLiveForm,
  useGetIdentity,
  useListContext,
  useTranslate,
  type Exporter,
} from "ra-core";
import { BulkActionsToolbar } from "@/components/admin/bulk-actions-toolbar";
import { CreateButton } from "@/components/admin/create-button";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";
import { SortButton } from "@/components/admin/sort-button";
import { PageSpinner } from "@/components/ui/page-spinner";
import { useState } from "react";

import type { Company, Contact, Sale, Tag } from "../types";
import { ContactEmpty } from "./ContactEmpty";
import { ContactListContent } from "./ContactListContent";
import { TopToolbar } from "../layout/TopToolbar";
import { CabecalhoPagina } from "../bento/ui";
import { getContactDetail } from "./contact-details";
import { SalesPersonFilter } from "../commons/SalesPersonFilter";
import { useSomenteLeitura } from "../commons/somenteLeitura";

export const ContactList = () => {
  const { identity } = useGetIdentity();
  const [salesPersonFilter, setSalesPersonFilter] = useState<string>('all');

  if (!identity) return <PageSpinner />;

  const permanentFilter = {
    ...(salesPersonFilter !== 'all' ? { user_id: salesPersonFilter } : {}),
  };

  return (
    <List
      title={false}
      actions={false}
      perPage={25}
      sort={{ field: "last_seen", order: "DESC" }}
      exporter={exporter}
      filter={permanentFilter}
    >
      <ContactListLayout
        salesPersonFilter={salesPersonFilter}
        onSalesPersonFilterChange={setSalesPersonFilter}
      />
    </List>
  );
};

const ContactListLayout = ({
  salesPersonFilter,
  onSalesPersonFilterChange,
}: {
  salesPersonFilter: string;
  onSalesPersonFilterChange: (v: string) => void;
}) => {
  const { somenteLeitura } = useSomenteLeitura();
  const translate = useTranslate();
  const { data, isPending, filterValues } = useListContext();
  const { identity } = useGetIdentity();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (!identity || isPending) return null;

  if (!data?.length && !hasFilters) return <ContactEmpty />;

  return (
    <div className="flex flex-col gap-4">
      <CabecalhoPagina
        titulo="Contatos"
        descricao="Todas as pessoas cadastradas, com ou sem venda."
        acoes={<ContactListActions />}
      />
      {/* Busca, vendedor e a lista num cartão só (Premium North). */}
      <div className="cartao bg-card">
        <div className="flex flex-col gap-2 border-b border-border p-4 sm:flex-row">
          <FilterLiveForm>
            <SearchInput
              source="q"
              placeholder={translate("atomic.contacts.searchPlaceholder")}
              className="w-full"
            />
          </FilterLiveForm>
          {/* Filtro por vendedor: super-admin (todos) ou gerente (só a equipe dele,
              escopo aplicado dentro do SalesPersonFilter). Vendedor comum não vê. */}
          {(identity?.is_super_admin || identity?.is_manager) && (
            <SalesPersonFilter
              value={salesPersonFilter}
              onValueChange={onSalesPersonFilterChange}
              className="sm:w-64"
            />
          )}
        </div>
        <ContactListContent />
      </div>
      {!somenteLeitura && <BulkActionsToolbar />}
    </div>
  );
};

const ContactListActions = () => {
  const { somenteLeitura } = useSomenteLeitura();
  return (
    <TopToolbar>
      <SortButton fields={["last_seen"]} />
      {!somenteLeitura && <ExportButton exporter={exporter} />}
      {!somenteLeitura && <CreateButton />}
    </TopToolbar>
  );
};

const exporter: Exporter<Contact> = async (records, fetchRelatedRecords) => {
  const companies = await fetchRelatedRecords<Company>(
    records,
    "company_id",
    "companies",
  );
  const sales = await fetchRelatedRecords<Sale>(records, "user_id", "users");
  const tags = await fetchRelatedRecords<Tag>(records, "tags", "tags");

  const contacts = records.map((contact) => {
    const sale = contact.user_id != null ? sales[contact.user_id] : undefined;
    const exportedContact = {
      ...contact,
      company:
        contact.company_id != null
          ? companies[contact.company_id].name
          : undefined,
      sales: sale ? sale.name : "",
      tags: contact.tags.map((tagId) => tags[tagId].name).join(", "),
      email: getContactDetail(contact, "email"),
      phone: getContactDetail(contact, "phone"),
    };
    return exportedContact;
  });
  return jsonExport(contacts, {}, (_err: any, csv: string) => {
    downloadCSV(csv, "contacts");
  });
};
